import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Flag,
  Receipt,
  BadgeCheck,
  Trash2,
  FileLock2,
  Zap,
  Building2,
  ChevronRight,
  Calendar,
  Handshake,
  Archive,
  AlarmClockOff,
  CalendarClock,
} from "lucide-react";
import { api, HERO_IMAGE, BRL, BRL_COMPACT, formatDateBR } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import PendingPaperworkAlert from "@/components/PendingPaperworkAlert";
import ExpiredBudgetsAlert from "@/components/ExpiredBudgetsAlert";
import NfaStalledAlert from "@/components/NfaStalledAlert";
import CaixaRepasseAlert from "@/components/CaixaRepasseAlert";
import QuickExecDialog from "@/components/QuickExecDialog";
import NewRegistrationsAlert from "@/components/NewRegistrationsAlert";
import SystemHealthWidget from "@/components/SystemHealthWidget";
import AgendaTodayCard from "@/components/AgendaTodayCard";
import IdeasChecklistDialog from "@/components/IdeasChecklistDialog";
import { ListChecks } from "lucide-react";
import CalendarDayCard from "@/components/calendar/CalendarDayCard";
import { BalancesStrip } from "@/components/BalancesStrip";
import { useAuth } from "@/lib/auth";

// Palette for school avatar circles — deterministic from school name
const SCHOOL_PALETTE = [
  { bg: "bg-violet-100", icon: "text-violet-600" },
  { bg: "bg-emerald-100", icon: "text-emerald-600" },
  { bg: "bg-amber-100", icon: "text-amber-600" },
  { bg: "bg-rose-100", icon: "text-rose-600" },
  { bg: "bg-sky-100", icon: "text-sky-600" },
  { bg: "bg-fuchsia-100", icon: "text-fuchsia-600" },
  { bg: "bg-indigo-100", icon: "text-indigo-600" },
  { bg: "bg-teal-100", icon: "text-teal-600" },
];

function schoolColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return SCHOOL_PALETTE[h % SCHOOL_PALETTE.length];
}

function budgetDesc(b) {
  if (b.service_type) return b.service_type;
  if (b.observation) return b.observation;
  return b.items?.[0]?.description || "Sem descrição";
}

function RecentBudgetCard({ b }) {
  const color = schoolColor(b.school_name);
  return (
    <Link
      to={`/orcamentos/${b.id}`}
      data-testid={`recent-budget-${b.number}`}
      className="block bg-white rounded-xl border border-slate-100 card-shadow p-3 hover:border-[#7c3aed]/40 transition-colors"
    >
      <div className="flex items-start gap-2.5">
        <div className={`w-10 h-10 rounded-full ${color.bg} flex items-center justify-center shrink-0`}>
          <Building2 className={`w-5 h-5 ${color.icon}`} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-heading text-[12px] font-black text-[#1e3a8a] uppercase truncate leading-tight">
                {b.school_name}
              </p>
              <p className="text-[10px] text-slate-500 leading-tight mt-0.5">
                N° {b.number} • {formatDateBR(b.budget_date)}
              </p>
              <p className="text-[11px] text-slate-700 line-clamp-1 mt-0.5">{budgetDesc(b)}</p>
            </div>
            <p className="font-heading text-[12px] font-black text-slate-900 whitespace-nowrap shrink-0">
              {BRL(b.total)}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            <StatusBadge status={b.status} />
            {b.faz_depois_paga && !["nota_fiscal_gerada", "nota_faturada", "reprovado", "deletado", "ex_sistema", "validade_expirada"].includes(b.status) && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                <Handshake className="w-2.5 h-2.5" /> FDP
              </span>
            )}
            {b.faz_depois_paga && ["nota_fiscal_gerada", "nota_faturada", "reprovado", "deletado", "ex_sistema", "validade_expirada"].includes(b.status) && (
              <span
                title="Emitido pelo sistema Faz Depois Paga"
                className="inline-flex items-center gap-1 text-[9px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full"
              >
                <Handshake className="w-2.5 h-2.5" /> ex-FDP
              </span>
            )}
            <span className="ml-auto text-[10px] text-slate-400 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {formatDateBR(b.updated_at)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function FdpHero({ fdp }) {
  return (
    <Link
      to="/fdp"
      className="min-w-0 flex flex-col items-start gap-1 p-2.5 rounded-xl text-white card-shadow hover:brightness-110 active:scale-[0.98] transition-[filter,transform]"
      style={{ background: "linear-gradient(120deg, #4c1d95 0%, #6d28d9 60%, #7c3aed 100%)" }}
      data-testid="fdp-hero-card"
    >
      <Handshake className="w-4 h-4" strokeWidth={2.4} />
      <span className="font-heading text-[11.5px] font-black leading-tight">Faz Depois Paga</span>
      <span className="text-[9.5px] text-white/85 leading-tight w-full truncate" data-testid="fdp-hero-stats">
        {fdp ? <>{fdp.active_budgets_count ?? 0} orç. · {BRL_COMPACT(fdp.active_budgets_total ?? 0)}{fdp.alerts.length > 0 && <span className="text-amber-200"> · {fdp.alerts.length}!</span>}</> : "…"}
      </span>
    </Link>
  );
}

const StatusCard = ({ icon: Icon, value, total, fdpCount, label, color, to, testid }) => {
  const hasBadge = typeof fdpCount === "number" && fdpCount > 0;
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group bg-white rounded-xl p-2.5 border border-slate-100 card-shadow hover:-translate-y-0.5 transition-all flex items-start gap-2 relative overflow-hidden"
    >
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: color }}>
        <Icon className="w-4 h-4 text-white" strokeWidth={2.4} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="font-heading text-xl font-black text-slate-900 leading-none">{value}</p>
          {hasBadge && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1 py-0.5 rounded leading-none shadow-sm"
              title={`${fdpCount} orçamento(s) no Faz Depois Paga`}
              data-testid={`${testid}-fdp-badge`}
            >
              <Handshake className="w-2.5 h-2.5" strokeWidth={3} />
              {fdpCount}
            </span>
          )}
        </div>
        <p
          className="text-[11px] text-slate-500 leading-tight mt-1 overflow-hidden"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
          title={label}
        >
          {label}
        </p>
        {typeof total === "number" && total > 0 && (
          <p className="text-[11px] font-bold text-emerald-700 tabular-nums mt-1 whitespace-nowrap overflow-hidden text-ellipsis" title={BRL(total)}>
            {BRL_COMPACT(total)}
          </p>
        )}
      </div>
    </Link>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [quickOpen, setQuickOpen] = useState(false);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [fdp, setFdp] = useState(null);
  const [recent, setRecent] = useState([]);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/fdp/summary").then((r) => setFdp(r.data)).catch(() => {});
    api
      .get("/budgets", { params: { sort: "updated_desc", limit: 5 } })
      .then((r) => setRecent(r.data))
      .catch(() => {});
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const firstName = user?.name?.split(" ")[0] || "visitante";
  const dateStr = now.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const timeStr = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-4">
      <BalancesStrip />

      {/* Hero banner compacto — texto à direita para não cobrir os rostos */}
      <div className="relative overflow-hidden rounded-2xl card-shadow bg-slate-200" data-testid="hero-banner">
        <img
          src={HERO_IMAGE}
          alt="Equipe AMC Escolas em frente a uma escola"
          className="w-full h-[88px] md:h-[110px] object-cover object-[center_18%]"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#1e3a8a]/85 via-[#1e3a8a]/55 to-transparent" />
        <button
          type="button"
          onClick={() => setIdeasOpen(true)}
          className="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-white/90 hover:bg-white px-2.5 py-1 text-[11px] font-bold text-violet-700 shadow active:scale-95 transition-transform"
          data-testid="btn-ideas-checklist"
        >
          <ListChecks className="w-3.5 h-3.5" /> Ideias
        </button>
        <IdeasChecklistDialog open={ideasOpen} onOpenChange={setIdeasOpen} />
        <div className="absolute inset-0 px-3 py-2 md:px-4 flex flex-col justify-center text-white items-end text-right">
          <span className="inline-block bg-white/15 backdrop-blur-sm px-2 py-0.5 rounded-full text-[8.5px] font-bold tracking-widest uppercase">
            Sistema AMC Escolas
          </span>
          <p className="font-heading text-[13px] md:text-base font-black leading-tight mt-1 capitalize">Bem-vindo, {firstName}</p>
          <p className="text-[10px] text-white/85 leading-tight capitalize">{dateStr} • {timeStr}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="dashboard-quick-tiles">
        <button
          type="button"
          onClick={() => setQuickOpen(true)}
          className="min-w-0 flex flex-col items-start gap-1 p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white card-shadow hover:brightness-105 active:scale-[0.98] transition-[filter,transform] text-left"
          data-testid="btn-quick-exec"
        >
          <Zap className="w-4 h-4" />
          <span className="font-heading font-black text-[11.5px] leading-tight">Execução rápida</span>
          <span className="text-[9.5px] text-white/90 leading-tight">Itens e fotos</span>
        </button>
        <FdpHero fdp={fdp} />
        <SystemHealthWidget tile />
      </div>
      <QuickExecDialog open={quickOpen} onOpenChange={setQuickOpen} />

      <AgendaTodayCard hideIfEmpty />
      <CalendarDayCard />

      <NewRegistrationsAlert />
      <CaixaRepasseAlert />
      <ExpiredBudgetsAlert />
      <NfaStalledAlert />

      {/* Papelada pendente alert — admins only */}
      <PendingPaperworkAlert onChanged={() => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {})} />

      {/* Status filter cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base font-bold text-[#1e3a8a]">Orçamentos por status</h3>
          <Link to="/orcamentos" className="text-xs text-[#7c3aed] font-semibold hover:underline">
            Ver tudo
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2.5">
          <StatusCard testid="st-aguardando" icon={Clock} value={stats.aguardando ?? 0} total={stats.totals?.aguardando ?? 0} fdpCount={stats.fdp_by_status?.aguardando ?? 0} label="Aguardando" color="#f97316" to="/orcamentos?status=aguardando" />
          <StatusCard testid="st-aprovado" icon={CheckCircle2} value={stats.aprovado ?? 0} total={stats.totals?.aprovado ?? 0} fdpCount={stats.fdp_by_status?.aprovado ?? 0} label="Aprovado" color="#16a34a" to="/orcamentos?status=aprovado" />
          <StatusCard testid="st-iniciando" icon={PlayCircle} value={stats.iniciando ?? 0} total={stats.totals?.iniciando ?? 0} fdpCount={stats.fdp_by_status?.iniciando ?? 0} label="Em andamento" color="#2563eb" to="/orcamentos?status=iniciando" />
          <StatusCard testid="st-finalizado" icon={Flag} value={stats.finalizado ?? 0} total={stats.totals?.finalizado ?? 0} fdpCount={stats.fdp_by_status?.finalizado ?? 0} label="Finalizado" color="#059669" to="/orcamentos?status=finalizado" />
          <StatusCard testid="st-fdp" icon={Handshake} value={stats.faz_depois_paga ?? 0} total={stats.faz_depois_paga_total ?? 0} label="Faz Depois Paga" color="#7c3aed" to="/orcamentos?status=fdp" />
          <StatusCard testid="st-reprovado" icon={XCircle} value={stats.reprovado ?? 0} total={stats.totals?.reprovado ?? 0} fdpCount={stats.fdp_by_status?.reprovado ?? 0} label="Reprovado" color="#ef4444" to="/orcamentos?status=reprovado" />
          <StatusCard testid="st-validade-expirada" icon={AlarmClockOff} value={stats.validade_expirada ?? 0} total={stats.totals?.validade_expirada ?? 0} label="Validade Expirada" color="#ea580c" to="/orcamentos?status=validade_expirada" />
          <StatusCard testid="st-nfg" icon={Receipt} value={stats.nota_fiscal_gerada ?? 0} total={stats.totals?.nota_fiscal_gerada ?? 0} fdpCount={stats.fdp_by_status?.nota_fiscal_gerada ?? 0} label="Nota fiscal gerada" color="#6366f1" to="/orcamentos?status=nota_fiscal_gerada" />
          <StatusCard testid="st-nfp" icon={BadgeCheck} value={stats.nota_faturada ?? 0} total={stats.totals?.nota_faturada ?? 0} fdpCount={stats.fdp_by_status?.nota_faturada ?? 0} label="Nota fiscal paga" color="#8b5cf6" to="/orcamentos?status=nota_faturada" />
          <StatusCard testid="st-gerado-por-fdp" icon={Handshake} value={stats.gerado_por_fdp ?? 0} label="Gerado por FDP" color="#64748b" to="/orcamentos?status=gerado_por_fdp" />
          <StatusCard testid="st-programados" icon={CalendarClock} value={stats.programados ?? 0} total={stats.totals?.programados ?? 0} label="Programados" color="#0284c7" to="/orcamentos?status=programados" />
          <StatusCard testid="st-nf-antecipada" icon={FileLock2} value={stats.nf_antecipada ?? 0} total={stats.totals?.nf_antecipada ?? 0} label="Nota gerada — em andamento" color="#4f46e5" to="/orcamentos?status=nf_antecipada" />
        </div>
      </div>

      {/* Orçamentos recentes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base font-bold text-[#1e3a8a]">Orçamentos recentes</h3>
          <Link to="/orcamentos" data-testid="link-ver-todos-orcamentos" className="text-xs text-[#7c3aed] font-semibold hover:underline inline-flex items-center gap-0.5">
            Ver todos <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 card-shadow p-6 text-center">
            <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-xs text-slate-500">Nenhum orçamento ainda. Crie o primeiro em "Orçamentos".</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="recent-budgets-list">
            {recent.map((b) => <RecentBudgetCard key={b.id} b={b} />)}
          </div>
        )}
      </div>
    </div>
  );
}
